import React, { useState, useMemo, useEffect, useRef } from "react";
import { LiveNumber } from "@/components/LiveNumber";
import ReactDOM from "react-dom";
import { SEUIL_TVA_FRANCHISE } from "@/utils/fiscal";
import { useSubscription } from "@/hooks/useSubscription";
import { useSimulationLimit } from "@/hooks/useSimulationLimit";
import PaywallCard from "@/components/PaywallCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { Switch } from "@/components/ui/switch";

const C = {
  navy: "#1B3A5C", teal: "#2A9D8F", blue: "#2E6DA4",
  light: "#EAF2FB", lightTeal: "#E8F5F4", danger: "#E63946",
  warning: "#F4A261", muted: "#6B85A3", border: "#D6E4F0",
  bg: "#F4F8FC", white: "#FFFFFF",
};

const fmt = (n: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

const TAUX_MICRO_CIPAV = 0.234;   // 23,2% URSSAF + 0,2% CFP
const TAUX_REEL_BNC = 0.40;       // ~40% estimation cotisations sur bénéfice net (URSSAF/CIPAV)
const SEUIL_TVA = SEUIL_TVA_FRANCHISE;

const DISCLAIMER_2026 = "Micro-BNC (CIPAV) : cotisations 23,2% + CFP 0,2% = 23,4% du CA — taux officiels 2026. BNC Réel : cotisations sociales estimées entre 35% et 45% du bénéfice selon votre niveau de revenus (fourchette prudente — votre expert-comptable affinera ce chiffre). Seuil Micro-BNC 2026-2028 : 83 600€. Ces estimations sont indicatives et ne remplacent pas les conseils d'un expert-comptable.";

const CHARGES_FIELDS = [
  { key: "loyer", icon: "🏠", label: "Loyer ou sous-location du cabinet", placeholder: 4800 },
  { key: "supervision", icon: "🧠", label: "Supervision clinique", placeholder: 1800 },
  { key: "analyse", icon: "🛋️", label: "Analyse personnelle", placeholder: 3000 },
  { key: "formations", icon: "📚", label: "Formations professionnelles", placeholder: 1200 },
  { key: "telecom", icon: "📱", label: "Téléphone et internet professionnels", placeholder: 360 },
  { key: "deplacement", icon: "🚗", label: "Frais de déplacement", placeholder: 600 },
  { key: "assurance", icon: "🛡️", label: "Assurance responsabilité civile pro", placeholder: 240 },
  { key: "cfe", icon: "🏛️", label: "CFE - Cotisation Foncière des Entreprises", placeholder: 500 },
  { key: "prevoyance", icon: "🏥", label: "Assurance prévoyance et maladie", placeholder: 600 },
  { key: "retraite", icon: "🏦", label: "Retraite complémentaire", placeholder: 1200 },
  { key: "cotisations", icon: "📋", label: "Cotisations ordinales et syndicales", placeholder: 150 },
  { key: "autres", icon: "➕", label: "Autres charges", placeholder: 0 },
];

type Regime = "micro" | "reel";

interface SimulateurProps {
  caAnnuel: number;
  onRetour: () => void;
  initialCharges?: Partial<Record<string, number>>;
  quickSimMode?: boolean;
}

export default function Simulateur({ caAnnuel, onRetour, initialCharges, quickSimMode }: SimulateurProps) {
  const [step, setStep] = useState(quickSimMode ? 2 : 1);
  const [whatIfCharge, setWhatIfCharge] = useState("");
  const [regime, setRegime] = useState<Regime>("reel");
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const { plan, trialUsed } = useSubscription();
  const proCta = trialUsed ? "Passer au Pro →" : "Passer au Pro — 14 jours gratuits →";
  const isPro = plan === "pro";
  const isEssentiel = !isPro;
  const { limitReached, increment } = useSimulationLimit("simulateur");
  const [showLimitModal, setShowLimitModal] = useState(false);
  const navigate = useNavigate();
  const hasCountedRef = useRef(false);

  useEffect(() => {
    if (isEssentiel && limitReached) setShowLimitModal(true);
  }, [isEssentiel, limitReached]);

  const TOOLTIP_TEXTS: Record<string, string> = {
    analyse: "Votre propre suivi psychologique en tant que praticien·ne — déductible en BNC classique. Non déductible en Micro-BNC.",
    cfe: "Taxe locale annuelle obligatoire pour tout indépendant. Montant entre 200€ et 1 000€ selon votre commune. Déductible en BNC classique.",
    prevoyance: "Couvre vos arrêts maladie et invalidité. En libéral vous n'avez pas de protection employeur — fortement recommandée. Déductible via contrat Madelin en BNC classique.",
    retraite: "Versements Madelin pour compléter votre retraite CIPAV. Déductible en BNC classique dans la limite de 10% du bénéfice + 15% entre 1 et 8 PASS.",
  };

  const handleTooltipClick = (e: React.MouseEvent, tooltipId: string) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({ top: rect.bottom + 8, left: Math.min(rect.left, window.innerWidth - 280) });
    setActiveTooltip(activeTooltip === tooltipId ? null : tooltipId);
  };

  useEffect(() => {
    if (!activeTooltip) return;
    const handler = () => setActiveTooltip(null);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [activeTooltip]);

  const [ca, setCa] = useState(String(caAnnuel || 0));
  const [charges, setCharges] = useState<Record<string, string>>(
    Object.fromEntries(CHARGES_FIELDS.map(f => [f.key, initialCharges?.[f.key] ? String(initialCharges[f.key]) : ""]))
  );

  const caNum = Number(ca) || 0;
  const whatIfAnnuel = (Number(whatIfCharge) || 0) * 12;
  const totalCharges = Object.values(charges).reduce((s, v) => s + (Number(v) || 0), 0) + (quickSimMode ? whatIfAnnuel : 0);
  const pctCharges = caNum > 0 ? (totalCharges / caNum) * 100 : 0;

  const simulation = useMemo(() => {
    // ── Micro-BNC : net = CA - cotisations CIPAV - charges réelles ──
    const cotisationsMicro = Math.round(caNum * TAUX_MICRO_CIPAV);
    const netMicroAnnuel = Math.max(0, caNum - cotisationsMicro - totalCharges);
    const netMicroMensuel = Math.round(netMicroAnnuel / 12);

    // ── Régime Réel (BNC) : net = bénéfice - cotisations sociales ──
    const beneficeReel = Math.max(0, caNum - totalCharges);
    const cotisationsReel = Math.round(beneficeReel * TAUX_REEL_BNC);
    const netReelAnnuel = Math.max(0, beneficeReel - cotisationsReel);
    const netReelMensuel = Math.round(netReelAnnuel / 12);

    const recommande: Regime = netReelAnnuel > netMicroAnnuel ? "reel" : "micro";
    const diff = Math.abs(netReelAnnuel - netMicroAnnuel);

    return {
      cotisationsMicro, netMicroAnnuel, netMicroMensuel,
      beneficeReel, cotisationsReel, netReelAnnuel, netReelMensuel,
      recommande, diff,
    };
  }, [caNum, totalCharges]);

  const activeNet = regime === "micro" ? simulation.netMicroAnnuel : simulation.netReelAnnuel;
  const activeNetMensuel = regime === "micro" ? simulation.netMicroMensuel : simulation.netReelMensuel;
  const isDeficit = caNum > 0 && totalCharges > caNum;

  const Stepper = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((s, i) => (
        <React.Fragment key={s}>
          {i > 0 && (
            <div className="w-12 sm:w-20 h-0.5 mx-1" style={{ backgroundColor: step >= s ? C.teal : C.border }} />
          )}
          <button
            onClick={() => { if (s < step || (s === 2 && step >= 1) || (s === 3 && step >= 2)) setStep(s); }}
            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all shrink-0"
            style={{
              backgroundColor: step === s ? C.teal : step > s ? C.lightTeal : C.white,
              color: step === s ? C.white : step > s ? C.teal : C.muted,
              border: `2px solid ${step >= s ? C.teal : C.border}`,
            }}
          >
            {step > s ? "✓" : s}
          </button>
        </React.Fragment>
      ))}
    </div>
  );

  const ChargesGauge = () => {
    const zones = [
      { max: 20, color: "#E63946", label: "Micro-BNC optimal" },
      { max: 34, color: "#F4A261", label: "Zone de transition" },
      { max: 100, color: "#2A9D8F", label: "BNC classique avantageux" },
    ];
    return (
      <div className="mt-4">
        <div className="flex justify-between text-xs mb-1" style={{ color: C.muted }}>
          <span>0%</span><span>20%</span><span>34%</span><span>100%</span>
        </div>
        <div className="relative h-4 rounded-full overflow-hidden flex">
          <div className="h-full" style={{ width: "20%", backgroundColor: zones[0].color, opacity: 0.3 }} />
          <div className="h-full" style={{ width: "14%", backgroundColor: zones[1].color, opacity: 0.3 }} />
          <div className="h-full" style={{ width: "66%", backgroundColor: zones[2].color, opacity: 0.3 }} />
          {caNum > 0 && (
            <div
              className="absolute top-0 h-full w-1 rounded"
              style={{ left: `${Math.min(pctCharges, 100)}%`, backgroundColor: C.navy, transition: "left 0.3s" }}
            />
          )}
        </div>
        <div className="flex gap-3 mt-2 flex-wrap">
          {zones.map(z => (
            <div key={z.label} className="flex items-center gap-1 text-xs" style={{ color: C.muted }}>
              <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: z.color }} />
              {z.label}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (isEssentiel && limitReached) {
    return (
      <>
        <Dialog open={showLimitModal} onOpenChange={setShowLimitModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ color: C.navy }}>Vous avez utilisé vos 3 simulations gratuites ce mois-ci</DialogTitle>
              <DialogDescription>Simulez sans limite avec le plan Pro.</DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 mt-4">
              <button
                onClick={() => navigate("/pricing")}
                className="w-full py-3 rounded-xl text-white font-bold text-sm"
                style={{ backgroundColor: C.teal }}
              >
                {proCta}
              </button>
              <button
                onClick={() => { setShowLimitModal(false); onRetour(); }}
                className="w-full py-3 rounded-xl text-sm font-semibold border"
                style={{ borderColor: C.border, color: C.navy }}
              >
                Revenir au dashboard
              </button>
            </div>
          </DialogContent>
        </Dialog>
        <PaywallCard
          icon="🔄"
          title="Vous avez utilisé vos 3 simulations gratuites ce mois-ci"
          description="Simulez sans limite avec le plan Pro."
          ctaLabel={proCta}
        />
      </>
    );
  }

  return (
    <>
    {activeTooltip && TOOLTIP_TEXTS[activeTooltip] && ReactDOM.createPortal(
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          backgroundColor: C.navy,
          color: C.white,
          padding: 12,
          borderRadius: 8,
          fontSize: 13,
          maxWidth: 260,
          zIndex: 40,
          lineHeight: 1.45,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        {TOOLTIP_TEXTS[activeTooltip]}
      </div>,
      document.body
    )}
    <div className="space-y-6">
      <Stepper />

      {/* ═══ BANNER WHAT-IF (quickSimMode) ═══ */}
      {quickSimMode && step === 2 && (
        <div className="rounded-2xl p-4 border-2" style={{ backgroundColor: C.lightTeal, borderColor: C.teal }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-base">🔮</span>
            <span className="font-bold text-sm" style={{ color: C.teal }}>Mode simulation — Testez l'impact d'un changement</span>
          </div>
          <p className="text-xs mb-3" style={{ color: C.muted }}>
            Vos données actuelles sont pré-remplies. Ajoutez une charge hypothétique pour voir son impact immédiat sur votre net.
          </p>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs font-semibold mb-1" style={{ color: C.navy }}>
                Nouvelle charge mensuelle (loyer, formation, assistant…)
              </label>
              <div className="relative">
                <input
                  type="number"
                  min={0}
                  placeholder="ex : 500"
                  value={whatIfCharge}
                  onChange={e => setWhatIfCharge(e.target.value)}
                  className="w-full rounded-xl border px-4 py-2.5 font-bold text-base outline-none"
                  style={{ borderColor: C.teal, color: C.navy, backgroundColor: C.white }}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold" style={{ color: C.muted }}>€/mois</span>
              </div>
            </div>
            {Number(whatIfCharge) > 0 && (
              <div className="text-center shrink-0 rounded-xl px-4 py-2" style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}>
                <div className="text-xs" style={{ color: C.muted }}>Impact annuel</div>
                <div className="font-bold text-base" style={{ color: "#E63946" }}>
                  −{(Number(whatIfCharge) * 12).toLocaleString("fr-FR")}&nbsp;€/an
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ ÉTAPE 1 ═══ */}
      {step === 1 && (
        <div className="space-y-5">
          <div className="text-center mb-2">
            <h2 className="text-xl font-bold" style={{ color: C.navy }}>Votre situation actuelle</h2>
            <p className="text-sm mt-1" style={{ color: C.muted }}>Ces informations nous permettent de calculer le régime le plus avantageux pour vous.</p>
          </div>

          {/* CA */}
          <div className="rounded-2xl border p-5 shadow-sm" style={{ backgroundColor: C.white, borderColor: C.border }}>
            <label className="block text-sm font-semibold mb-2" style={{ color: C.navy }}>
              💰 Chiffre d'affaires annuel
            </label>
            <input
              type="number"
              value={ca}
              onChange={e => setCa(e.target.value)}
              className="w-full h-12 rounded-xl border px-4 text-lg font-semibold focus:outline-none focus:ring-2"
              style={{ borderColor: C.border, color: C.navy } as any}
            />
            <div className="mt-2">
              {caNum <= SEUIL_TVA ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: C.lightTeal, color: C.teal }}>
                  ✅ Sous le seuil TVA
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: "#FFF8F0", color: "#C46200" }}>
                  ⚠️ Seuil TVA dépassé
                </span>
              )}
            </div>
          </div>

          {/* Charges */}
          <div className="rounded-2xl border p-5 shadow-sm" style={{ backgroundColor: C.white, borderColor: C.border }}>
            <h3 className="font-semibold text-base mb-1" style={{ color: C.navy }}>Vos charges professionnelles annuelles estimées</h3>
            <p className="text-xs mb-4" style={{ color: C.muted }}>Indiquez 0 si vous n'avez pas cette charge.</p>
            <div className="space-y-3">
              {CHARGES_FIELDS.map(f => (
                <div key={f.key} className="flex items-center gap-3">
                  <span className="text-xl w-8 text-center shrink-0">{f.icon}</span>
                  <div className="flex-1 min-w-0">
                    <label className="flex items-center gap-1 text-sm font-medium truncate" style={{ color: C.navy }}>
                      {f.label}
                      {TOOLTIP_TEXTS[f.key] && (
                        <button
                          type="button"
                          onClick={(e) => handleTooltipClick(e, f.key)}
                          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-bold cursor-pointer shrink-0"
                          style={{ backgroundColor: C.border, color: C.muted, position: "relative", zIndex: 1 }}
                        >?</button>
                      )}
                    </label>
                    <input
                      type="number"
                      value={charges[f.key]}
                      onChange={e => setCharges(prev => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder={f.placeholder > 0 ? `${f.placeholder} €/an` : "0 €"}
                      className="w-full h-9 rounded-lg border px-3 text-sm mt-1 focus:outline-none focus:ring-2"
                      style={{ borderColor: C.border, color: C.navy } as any}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Récap charges */}
            <div className="rounded-xl border p-4 mt-5" style={{ backgroundColor: C.light, borderColor: C.border }}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold" style={{ color: C.navy }}>Total des charges</span>
                <span className="text-xl font-bold" style={{ color: C.navy }}>{fmt(totalCharges)}</span>
              </div>
              {caNum > 0 && (
                <div className="text-xs mt-1" style={{ color: C.muted }}>
                  Soit {pctCharges.toFixed(1)}% de votre CA
                </div>
              )}
              <ChargesGauge />
            </div>
          </div>

          {/* ── Live preview — régime recommandé (réactif pendant la saisie) ── */}
          {caNum > 0 && (
            <div
              className="rounded-xl p-4 mt-2"
              style={{
                backgroundColor: simulation.recommande === "micro" ? C.lightTeal : C.light,
                border: `1.5px solid ${simulation.recommande === "micro" ? C.teal : C.border}`,
                transition: "background-color 0.35s ease, border-color 0.35s ease",
              }}
            >
              <div className="text-xs font-bold mb-1.5" style={{ color: simulation.recommande === "micro" ? C.teal : C.navy }}>
                {simulation.recommande === "micro" ? "✅ Micro-BNC recommandé" : "📊 Régime Réel recommandé"}
                {" · "}
                <span style={{ fontWeight: 400, color: C.muted }}>
                  +{fmt(simulation.diff)}/an vs l'autre régime
                </span>
              </div>
              <div className="font-bold" style={{ fontSize: 22 }}>
                <LiveNumber
                  value={simulation.recommande === "micro" ? simulation.netMicroMensuel : simulation.netReelMensuel}
                  fmt={fmt}
                  baseColor={C.navy}
                />
                <span className="text-sm font-semibold ml-1" style={{ color: C.muted }}>/mois net estimé</span>
              </div>
            </div>
          )}

          <button
            onClick={() => {
              if (isEssentiel && !hasCountedRef.current) {
                hasCountedRef.current = true;
                increment();
              }
              setStep(2);
            }}
            className="w-full h-12 rounded-xl font-bold text-base transition-all hover:opacity-90"
            style={{ backgroundColor: C.teal, color: C.white }}
          >
            Calculer ma simulation →
          </button>
        </div>
      )}

      {/* ═══ ÉTAPE 2 ═══ */}
      {step === 2 && (
        <div className="space-y-5">
          <div className="text-center mb-2">
            <h2 className="text-xl font-bold" style={{ color: C.navy }}>Votre simulation personnalisée</h2>
            <p className="text-sm mt-1" style={{ color: C.muted }}>
              Basé sur un CA de {fmt(caNum)} et {fmt(totalCharges)} de charges annuelles.
            </p>
          </div>

          {/* ── Toggle régime fiscal ── */}
          <div className="rounded-2xl border p-4 shadow-sm flex items-center justify-between" style={{ backgroundColor: C.white, borderColor: C.border }}>
            <div className="flex items-center gap-3">
              <span className="text-lg">{regime === "micro" ? "📋" : "📊"}</span>
              <div>
                <div className="text-sm font-semibold" style={{ color: C.navy }}>
                  {regime === "micro" ? "Micro-BNC" : "Régime Réel (BNC)"}
                </div>
                <div className="text-xs" style={{ color: C.muted }}>
                  {regime === "micro" ? "URSSAF + CFP 23,4% du CA" : "Cotisations estimées 35-45% du bénéfice"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium" style={{ color: regime === "micro" ? C.teal : C.muted }}>Micro</span>
              <Switch
                checked={regime === "reel"}
                onCheckedChange={(checked) => setRegime(checked ? "reel" : "micro")}
              />
              <span className="text-xs font-medium" style={{ color: regime === "reel" ? C.teal : C.muted }}>Réel</span>
            </div>
          </div>

          {/* Alerte déficit */}
          {isDeficit && (
            <div className="rounded-xl p-4 flex items-start gap-3" style={{ backgroundColor: "#FFF0F0", border: `1px solid ${C.danger}` }}>
              <span className="text-xl">⚠️</span>
              <div>
                <div className="font-semibold text-sm" style={{ color: C.danger }}>Attention : vos charges dépassent votre CA</div>
                <div className="text-xs mt-1" style={{ color: C.muted }}>
                  Charges de {fmt(totalCharges)} pour un CA de {fmt(caNum)}. Le revenu net est ramené à 0&nbsp;€.
                </div>
              </div>
            </div>
          )}

          {/* Carte résultat principal */}
          <div
            className="rounded-2xl border-2 p-6 shadow-sm"
            style={{ backgroundColor: C.white, borderColor: C.teal, borderWidth: 3 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <span className="text-2xl">{regime === "micro" ? "📋" : "📊"}</span>
              <h3 className="font-bold text-lg" style={{ color: C.navy }}>Revenu net estimé</h3>
            </div>
            <div className="text-4xl font-bold mb-1" style={{ fontSize: 36 }}>
              <LiveNumber value={activeNetMensuel} fmt={fmt} baseColor={C.teal} />
            </div>
            <div className="text-sm mb-1" style={{ color: C.muted }}>
              par mois · <LiveNumber value={activeNet} fmt={fmt} baseColor={C.muted} /> /an
            </div>
            <div className="text-xs" style={{ color: C.muted }}>
              {regime === "micro" ? "Micro-BNC (URSSAF + CFP 23,4%)" : "Réel BNC (Cotisations estimées 35–45% du bénéfice)"}
            </div>

            <div className="border-t mt-4 pt-4 space-y-2 text-xs" style={{ borderColor: C.border, color: C.muted }}>
              <div className="flex justify-between">
                <span>Chiffre d'affaires</span>
                <span style={{ color: C.navy }}>{fmt(caNum)}</span>
              </div>
              {regime === "micro" ? (
                <>
                  <div className="flex justify-between">
                    <span>Cotisations CIPAV (23,4% du CA)</span>
                    <span style={{ color: C.danger }}>-{fmt(simulation.cotisationsMicro)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Charges professionnelles</span>
                    <span style={{ color: C.danger }}>-{fmt(totalCharges)}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span>Charges professionnelles</span>
                    <span style={{ color: C.danger }}>-{fmt(totalCharges)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Bénéfice avant cotisations</span>
                    <span style={{ color: C.navy }}>{fmt(simulation.beneficeReel)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Cotisations sociales (estimées 35–45% du bénéfice)</span>
                    <span style={{ color: C.danger }}>-{fmt(simulation.cotisationsReel)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between font-semibold pt-2 border-t" style={{ borderColor: C.border, color: C.navy }}>
                <span>Revenu net annuel</span>
                <span>{fmt(activeNet)}</span>
              </div>
            </div>
          </div>

          {/* Comparaison rapide */}
          <div className="rounded-xl p-4 flex items-start gap-3" style={{ backgroundColor: C.light, border: `1px solid ${C.border}` }}>
            <span className="text-xl">💡</span>
            <div>
              {simulation.recommande === regime ? (
                <div className="font-semibold text-sm" style={{ color: C.navy }}>
                  ✅ Ce régime est le plus avantageux pour vous ({fmt(simulation.diff)} de plus que l'autre régime).
                </div>
              ) : (
                <div className="font-semibold text-sm" style={{ color: C.navy }}>
                  Le régime {simulation.recommande === "micro" ? "Micro-BNC" : "Réel BNC"} serait plus avantageux : +{fmt(simulation.diff)}/an.
                  <button
                    onClick={() => setRegime(simulation.recommande)}
                    className="ml-2 underline text-xs"
                    style={{ color: C.teal }}
                  >
                    Basculer →
                  </button>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => setStep(3)}
            className="w-full h-12 rounded-xl font-bold text-base transition-all hover:opacity-90"
            style={{ backgroundColor: C.teal, color: C.white }}
          >
            Comprendre les détails →
          </button>
        </div>
      )}

      {/* ═══ ÉTAPE 3 ═══ */}
      {step === 3 && (
        <div className="space-y-5">
          <div className="text-center mb-2">
            <h2 className="text-xl font-bold" style={{ color: C.navy }}>Ce que cela signifie pour vous</h2>
          </div>

          <details className="rounded-2xl border shadow-sm overflow-hidden group" style={{ borderColor: C.border, backgroundColor: C.white }}>
            <summary className="p-5 cursor-pointer font-semibold text-sm flex items-center gap-2 list-none" style={{ color: C.navy }}>
              <span className="text-lg">✅</span>
              Les avantages de votre régime actuel (Micro-BNC)
              <span className="ml-auto text-xs transition-transform group-open:rotate-180" style={{ color: C.muted }}>▼</span>
            </summary>
            <div className="px-5 pb-5 space-y-3 text-sm" style={{ color: C.navy }}>
              <div className="flex items-start gap-3">
                <span className="text-teal-500 mt-0.5">•</span>
                <p><strong>Simplicité administrative totale</strong> — aucune comptabilité à tenir, aucun expert-comptable requis.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-teal-500 mt-0.5">•</span>
                <p><strong>Déclaration trimestrielle en 5 minutes</strong> sur urssaf.fr.</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-teal-500 mt-0.5">•</span>
                <p><strong>Idéal si vos charges réelles sont inférieures à 34% de votre CA</strong> — l'abattement forfaitaire couvre déjà plus que vos dépenses.</p>
              </div>
            </div>
          </details>

          <details className="rounded-2xl border shadow-sm overflow-hidden group" style={{ borderColor: C.border, backgroundColor: C.white }}>
            <summary className="p-5 cursor-pointer font-semibold text-sm flex items-center gap-2 list-none" style={{ color: C.navy }}>
              <span className="text-lg">💡</span>
              Quand envisager le BNC classique
              <span className="ml-auto text-xs transition-transform group-open:rotate-180" style={{ color: C.muted }}>▼</span>
            </summary>
            <div className="px-5 pb-5 space-y-3 text-sm" style={{ color: C.navy }}>
              <p>
                Dans votre situation, le BNC classique devient avantageux si vos charges annuelles dépassent un certain seuil.
                Actuellement vos charges sont de <strong>{fmt(totalCharges)}</strong>.
              </p>
              <div className="space-y-3 mt-3">
                <div className="flex items-start gap-3">
                  <span style={{ color: C.warning }}>⚠️</span>
                  <p><strong>Comptabilité rigoureuse obligatoire</strong> — vous devez conserver tous vos justificatifs.</p>
                </div>
                <div className="flex items-start gap-3">
                  <span style={{ color: C.warning }}>⚠️</span>
                  <p><strong>Expert-comptable recommandé</strong> — budget moyen de 800 à 1 500 €/an.</p>
                </div>
                <div className="flex items-start gap-3">
                  <span style={{ color: C.warning }}>⚠️</span>
                  <p><strong>Transition administrative</strong> la première année — quelques démarches supplémentaires.</p>
                </div>
                <div className="flex items-start gap-3">
                  <span style={{ color: C.teal }}>✅</span>
                  <p><strong>Accès à la déduction Madelin</strong> pour optimiser votre retraite.</p>
                </div>
              </div>
            </div>
          </details>

          <div className="rounded-xl p-4 border-l-4" style={{ backgroundColor: C.light, borderLeftColor: C.navy }}>
            <p className="text-xs" style={{ color: C.navy }}>
              📌 {DISCLAIMER_2026}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => setStep(1)}
              className="flex-1 h-12 rounded-xl font-bold text-sm border-2 transition-all hover:opacity-80"
              style={{ borderColor: C.navy, color: C.navy, backgroundColor: "transparent" }}
            >
              ← Modifier ma simulation
            </button>
            <button
              onClick={onRetour}
              className="flex-1 h-12 rounded-xl font-bold text-sm transition-all hover:opacity-90"
              style={{ backgroundColor: C.teal, color: C.white }}
            >
              Retour au tableau de bord
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
}
